import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import Gleap from 'gleap';
import PropTypes from 'prop-types';

import config from 'config/config';
import AuthSessionContext from 'contexts/AuthSessionProvider/AuthSessionContext';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import localStorageUser from 'utils/localStorageUser';
import sessionStorageUser from 'utils/sessionStorageUser';

const Auth0Session = ({ children }) => {
  // #region [auth]
  const {
    user,
    isLoading,
    error,
    isAuthenticated: isIDPAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();
  // #endregion

  // #region [errors]
  const dispatchGlobalError = useGlobalErrorHandler();
  // #endregion

  // #region [states]
  const [isPasswordAuthenticated, setIsPasswordAuthenticated] = useState(
    () => !!localStorageUser.getAccessToken(),
  );
  // #endregion

  // #region [callbacks]
  const clearLocalState = useCallback((isSilent = false) => {
    try {
      if (process.env.NODE_ENV === 'production') {
        if (typeof Gleap.isUserIdentified === 'function' && Gleap.isUserIdentified()) {
          Gleap.clearIdentity();
          Gleap.clearCustomData();
        }
      }
    } catch (err) {
      console.error('Gleap cleanup failed', err);
    } finally {
      localStorageUser.removeAuthTokens();
      sessionStorageUser.setFinalcadTempRegistrationInfo(null);
      sessionStorageUser.setIsSignUp(null);
      if (!isSilent) setIsPasswordAuthenticated(false);
    }
  }, []);

  const storeAuthTokens = useCallback(({ accessToken, idToken, refreshToken }) => {
    localStorageUser.setAuthTokens({
      accessToken,
      idToken,
      refreshToken,
    });
  }, []);

  const fetchAccessToken = useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (isIDPAuthenticated) {
        const accessToken = await getAccessTokenSilently({
          cacheMode: forceRefresh ? 'off' : 'on',
        });
        try {
          const idTokenClaims = await getIdTokenClaims();
          storeAuthTokens({
            accessToken,
            // eslint-disable-next-line no-underscore-dangle
            idToken: idTokenClaims?.__raw,
          });
        } catch (err) {
          console.error('Failed to mirror IDP tokens to localStorage', err);
        }
        return accessToken;
      }
      const authTokens = localStorageUser.getAuthTokens();
      if (!authTokens?.accessToken) return null;

      const exp = authTokens.accessToken.claims?.exp;
      const isExpired = exp && exp * 1000 < Date.now();

      // Cannot refresh without a refresh token; clear state to avoid keeping invalid tokens.
      if (forceRefresh && !authTokens.refreshToken) {
        clearLocalState();
        return null;
      }

      if ((isExpired || forceRefresh) && authTokens.refreshToken) {
        const response = await fetch(config.auth0.tokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: config.auth0.clientId,
            grant_type: config.auth0.refreshTokenGrantType,
            refresh_token: authTokens.refreshToken,
          }),
        });
        if (response.ok) {
          const newTokens = await response.json();
          storeAuthTokens({
            accessToken: newTokens.access_token,
            idToken: newTokens.id_token,
            refreshToken: newTokens.refresh_token,
          });
          return newTokens.access_token;
        }
        clearLocalState();
        return null;
      }

      return authTokens.accessToken.raw;
    },
    [
      isIDPAuthenticated,
      getAccessTokenSilently,
      getIdTokenClaims,
      storeAuthTokens,
      clearLocalState,
    ],
  );

  const loginWithIDP = useCallback(
    async (options = { cacheMode: 'on' }) => {
      try {
        const accessToken = await getAccessTokenSilently({ cacheMode: options.cacheMode });
        const idTokenClaims = await getIdTokenClaims();
        // eslint-disable-next-line no-underscore-dangle
        storeAuthTokens({ accessToken, idToken: idTokenClaims?.__raw });
      } catch (err) {
        dispatchGlobalError({ error: err });
      }
    },
    [getAccessTokenSilently, getIdTokenClaims, storeAuthTokens, dispatchGlobalError],
  );

  const loginWithPassword = useCallback(
    async (email, password) => {
      Object.keys(localStorage)
        .filter(key => key.startsWith('@@auth0spajs@@'))
        .forEach(key => localStorage.removeItem(key));
      const response = await fetch(config.auth0.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: config.auth0.clientId,
          username: email,
          password,
          audience: config.auth0.audience,
          scope: config.auth0.scope,
          grant_type: config.auth0.passwordRealmGrantType,
          realm: config.auth0.realm,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(
          errorData.error_description || errorData.error || 'Authentication failed',
        );
        error.code = errorData.error;
        throw error;
      }

      const tokenData = await response.json();
      setIsPasswordAuthenticated(true);
      storeAuthTokens({
        accessToken: tokenData.access_token,
        idToken: tokenData.id_token,
        refreshToken: tokenData.refresh_token,
      });
      return tokenData;
    },
    [storeAuthTokens],
  );

  const handleRedirectCallback = useCallback(async () => {}, []);

  const logout = useCallback(
    async (openUrl = false) => {
      let refreshToken = null;
      try {
        const authTokens = localStorageUser.getAuthTokens();
        refreshToken = authTokens?.refreshToken;
      } catch (err) {
        console.error('Could not read tokens before logout', err);
      }

      if (isIDPAuthenticated && openUrl) {
        clearLocalState(true);
        await auth0Logout({ openUrl });
        return;
      }

      if (isIDPAuthenticated) {
        clearLocalState(true);
        auth0Logout({
          logoutParams: {
            returnTo: `${window.location.origin}/login`,
          },
        });
        return;
      }

      try {
        if (refreshToken) {
          const response = await fetch(`https://${config.auth0.domain}/oauth/revoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              client_id: config.auth0.clientId,
              token: refreshToken,
            }),
          });
          if (!response.ok) {
            throw new Error(`Auth0 revoke failed with status ${response.status}`);
          }
        }
      } catch (err) {
        dispatchGlobalError({ error: err });
      } finally {
        clearLocalState(true);
        window.location.assign('/login');
      }
    },
    [isIDPAuthenticated, auth0Logout, dispatchGlobalError, clearLocalState],
  );
  // #endregion

  // #region [effects]
  // Expose a global session object for use in non-React code (e.g. RTK Query baseQuery).
  useEffect(
    () => {
      const session = { fetchAccessToken, logout };
      window.finalcadAuthSession = session;
      return () => {
        if (window.finalcadAuthSession === session) {
          delete window.finalcadAuthSession;
        }
      };
    },
    [fetchAccessToken, logout],
  );

  // Auth0 SDK keeps tokens in its own cache; mirror them into auth_tokens so the
  // bearer is available before any protected RTK Query fires after an SSO callback.
  useEffect(
    () => {
      (async () => {
        if (isIDPAuthenticated && !localStorageUser.getAccessToken()) {
          await loginWithIDP();
        }
      })();
    },
    [isIDPAuthenticated, loginWithIDP],
  );
  // #endregion

  // #region [memos]
  const value = useMemo(
    () => ({
      user,
      isLoading: isPasswordAuthenticated ? false : isLoading,
      error,
      isAuthenticated: isIDPAuthenticated || isPasswordAuthenticated,
      isIDPUser: isIDPAuthenticated,
      clearLocalState,
      fetchAccessToken,
      loginWithIDP,
      loginWithPassword,
      loginWithRedirect,
      handleRedirectCallback,
      logout,
    }),
    [
      user,
      isLoading,
      error,
      isIDPAuthenticated,
      isPasswordAuthenticated,
      clearLocalState,
      fetchAccessToken,
      loginWithIDP,
      loginWithPassword,
      loginWithRedirect,
      handleRedirectCallback,
      logout,
    ],
  );
  // #endregion

  // #region [render]
  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
  // #endregion
};

Auth0Session.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Auth0Session;
